/**
 * routes.ts — the route table, keyed by instance mode.
 *
 * decision-2026-07-25-broker-mode-split (option C + B): the operator provisioning
 * surface is gated on MODE, structurally — not on roles. Before the split every
 * mode shared one implicit route table and `/admin/sandbox-provisioning` was
 * reachable on any deployment that authenticated, with `hasRole()` as the only
 * thing standing between a tenant user and Provision / Promote / Decommission.
 *
 * Registration now happens here and nowhere else:
 *   • AppShell renders a route only if `isRouteRegistered(mode, route)`.
 *   • AdminMenu derives its items from this table.
 *   • An unregistered route resolves to NOT FOUND — not to a role prompt. A role
 *     prompt would tell a tenant user the capability exists and they lack the role;
 *     the capability does not exist on that deployment at all (adr-075 D11).
 *
 * Role checks stay in place on top of this, as defense in depth. Mode decides what
 * EXISTS; roles decide who may use what exists.
 *
 * Restated gate 9(a) tests against this module directly.
 */

import type { InstanceMode } from '../config/schema';
import { META_MODE, TENANT_MODE } from '../config/schema';
import type { Route } from './Router';

/** Routes every authenticated broker surface carries, meta or tenant. */
const COMMON_BROKER_ROUTES: Route[] = [
  '/',
  '/dashboard',
  '/profile',
  '/cart',
  '/admin',
  '/admin/sub-clients',
  '/admin/subscriptions',
  '/admin/orders',
  '/admin/audit-log',
];

/**
 * The operator provisioning surface. Meta broker only — this is the whole point of
 * the split. Do not add it to any other mode's table without a new decision.
 */
export const OPERATOR_ROUTES: Route[] = ['/admin/sandbox-provisioning'];

/** The tenant-side sandbox request surface. Tenant brokers only. */
export const TENANT_ROUTES: Route[] = ['/sandbox/request'];

/** Minimal surfaces for the pre-split, non-authenticated modes — unchanged. */
const TIER0_ROUTES: Route[] = ['/', '/dashboard', '/profile'];
const REFERENCE_ROUTES: Route[] = ['/'];

export function routesForMode(mode: InstanceMode): Route[] {
  switch (mode) {
    case META_MODE:
      return [...COMMON_BROKER_ROUTES, ...OPERATOR_ROUTES];
    case TENANT_MODE:
      return [...COMMON_BROKER_ROUTES, ...TENANT_ROUTES];
    case 'tier-1-hub':
      return [...COMMON_BROKER_ROUTES];
    case 'tier-0':
      return TIER0_ROUTES;
    case 'reference':
      return REFERENCE_ROUTES;
    default:
      return REFERENCE_ROUTES;
  }
}

/**
 * Dynamic-segment routes are matched by shape, not listed literally. They ride on
 * their parent surface's registration.
 */
const DYNAMIC_ROUTE_PATTERNS: { re: RegExp; requires: Route }[] = [
  { re: /^\/admin\/sub-clients\/[^/]+$/, requires: '/admin/sub-clients' },
  { re: /^\/apps\/[a-z0-9-]+\/operations$/, requires: '/admin' },
];

export function isRouteRegistered(mode: InstanceMode, route: string): boolean {
  const table = routesForMode(mode);
  if ((table as string[]).includes(route)) return true;
  for (const { re, requires } of DYNAMIC_ROUTE_PATTERNS) {
    if (re.test(route) && (table as string[]).includes(requires)) return true;
  }
  return false;
}

/** Convenience for the surfaces that ask "does this deployment provision at all?". */
export function hasOperatorProvisioning(mode: InstanceMode): boolean {
  return routesForMode(mode).includes(OPERATOR_ROUTES[0]);
}
